import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorDetails = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };
    
    console.error('🔴 Error Boundary caught:', errorDetails);
    
    // TODO: Send to error tracking service (Sentry, LogRocket, etc.)
    // Example: sendToErrorTracking(errorDetails);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-background">
          <div className="max-w-md w-full space-y-6 text-center">
            <div className="space-y-2">
              <h1 className="text-3xl font-bold text-foreground">Oops! Something went wrong</h1>
              <p className="text-muted-foreground">
                We're sorry for the inconvenience. The application encountered an unexpected error.
              </p>
            </div>
            
            {this.state.error && (
              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-left space-y-2">
                <p className="text-sm font-mono text-destructive break-all">
                  {this.state.error.message}
                </p>
                <p className="text-xs text-muted-foreground">
                  Error Code: {this.state.error.name || 'UNKNOWN'} - {new Date().toISOString()}
                </p>
                <p className="text-xs text-muted-foreground">
                  Please share this information with support if the issue persists.
                </p>
              </div>
            )}
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={this.handleReset} size="lg">
                Return to Home
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => window.location.reload()}
              >
                Reload Page
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
